import React, { useState, useEffect, useRef } from 'react'
import { styled } from '@mui/material/styles'
import { DEV_COLOR, TEST_COLOR, PROD_COLOR } from '../assets/jss/material-dashboard-react'
import { Checkbox, FormControlLabel, Grid } from '@mui/material'
import Widget from '../components/Widget/Widget'

const PREFIX = 'Home'

const classes = {
  statsRoot: `${PREFIX}-statsRoot`,
  graphStyle: `${PREFIX}-graphStyle`,
  divBg: `${PREFIX}-divBg`,
  floatingActionBar: `${PREFIX}-floatingActionBar`,
  dragHandle: `${PREFIX}-dragHandle`,
}

const Root = styled('div')(({ theme }) => ({
  [`& .${classes.statsRoot}`]: {
    display: 'flex',
    flexWrap: 'wrap',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },

  [`& .${classes.graphStyle}`]: {
    '& .ct-line': { strokeWidth: '2px' },
    '& .ct-grids line': { stroke: 'grey' },
    '& .ct-labels span': { color: 'grey' },
    '& .ct-series-a .ct-line': {
      stroke: DEV_COLOR,
    },
    '& .ct-series-b .ct-line': {
      stroke: TEST_COLOR,
    },
    '& .ct-series-c .ct-line': {
      stroke: PROD_COLOR,
    },
    '& .ct-series-a .ct-bar': {
      stroke: DEV_COLOR,
    },
    '& .ct-series-b .ct-bar': {
      stroke: TEST_COLOR,
    },
    '& .ct-series-c .ct-bar': {
      stroke: PROD_COLOR,
    },
    '& .ct-series-a .ct-slice-donut': {
      stroke: DEV_COLOR,
    },
    '& .ct-series-b .ct-slice-donut': {
      stroke: '#efefef',
      opacity: 10.0,
    },
    height: '100%',
  },
  [`&.${classes.divBg}`]: {
    padding: 5,
    backgroundColor: 'transparent',
    [theme.breakpoints.down('sm')]: {
      padding: 2,
    },
  },
  [`& .${classes.floatingActionBar}`]: {
    position: 'fixed',
    bottom: theme.spacing(1.5),
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 1000,
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(18, 18, 18, 0.95)' : 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(10px)',
    borderRadius: '12px',
    border: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
    padding: theme.spacing(1.5, 3),
    boxShadow:
      theme.palette.mode === 'dark'
        ? '0 10px 15px -3px rgba(0, 0, 0, 0.4), 0 4px 6px -2px rgba(0, 0, 0, 0.3)'
        : '0 10px 15px -3px rgba(0, 0, 0, 0.2), 0 4px 6px -2px rgba(0, 0, 0, 0.15)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1, 2),
      bottom: theme.spacing(2),
      borderRadius: '10px',
    },
  },
  [`& .${classes.dragHandle}`]: {
    width: '40px',
    height: '4px',
    borderRadius: '2px',
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
    cursor: 'grab',
    marginBottom: theme.spacing(0.5),
    '&:active': {
      cursor: 'grabbing',
    },
  },
}))

const baseWidgetParams = {
  timeRange: 7,
}

const Home = () => {
  const [enabledKeys, setEnabledKeys] = useState(['dev', 'test', 'prod'])
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })
  const [hasBeenDragged, setHasBeenDragged] = useState(false)
  const actionBarRef = useRef<HTMLDivElement>(null)
  const rafRef = useRef<number | null>(null)

  const toggleStage = (stageName: string) => {
    let newKeys = [...enabledKeys]
    const index = newKeys.indexOf(stageName)
    if (index < 0) {
      newKeys.push(stageName)
    } else {
      newKeys.splice(index, 1)
    }
    setEnabledKeys(newKeys)
  }

  const startDrag = (clientX: number, clientY: number) => {
    setIsDragging(true)
    setHasBeenDragged(true)
    if (actionBarRef.current) {
      const rect = actionBarRef.current.getBoundingClientRect()
      setDragOffset({
        x: clientX - rect.left,
        y: clientY - rect.top,
      })
    }
  }

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    startDrag(e.clientX, e.clientY)
  }

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    const touch = e.touches[0]
    startDrag(touch.clientX, touch.clientY)
  }

  const moveDrag = (clientX: number, clientY: number) => {
    if (!isDragging || !actionBarRef.current) return

    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current)
    }

    rafRef.current = requestAnimationFrame(() => {
      if (actionBarRef.current) {
        const rect = actionBarRef.current.getBoundingClientRect()
        const appBarHeight = 64

        let newX = clientX - dragOffset.x
        let newY = clientY - dragOffset.y

        const minX = 0
        const maxX = window.innerWidth - rect.width
        const minY = appBarHeight
        const maxY = window.innerHeight - rect.height

        newX = Math.max(minX, Math.min(newX, maxX))
        newY = Math.max(minY, Math.min(newY, maxY))

        setPosition({ x: newX, y: newY })
      }
    })
  }

  const handleMouseMove = (e: MouseEvent) => {
    moveDrag(e.clientX, e.clientY)
  }

  const handleTouchMove = (e: TouchEvent) => {
    e.preventDefault()
    const touch = e.touches[0]
    moveDrag(touch.clientX, touch.clientY)
  }

  const endDrag = () => {
    setIsDragging(false)
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }
  }

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove)
      window.addEventListener('mouseup', endDrag)
      window.addEventListener('touchmove', handleTouchMove, { passive: false })
      window.addEventListener('touchend', endDrag)
      return () => {
        window.removeEventListener('mousemove', handleMouseMove)
        window.removeEventListener('mouseup', endDrag)
        window.removeEventListener('touchmove', handleTouchMove)
        window.removeEventListener('touchend', endDrag)
        if (rafRef.current !== null) {
          cancelAnimationFrame(rafRef.current)
          rafRef.current = null
        }
      }
    }
  }, [isDragging, dragOffset])

  return (
    <Root className={classes.divBg}>
      <div
        ref={actionBarRef}
        className={classes.floatingActionBar}
        style={{
          left: hasBeenDragged ? `${position.x}px` : '50%',
          bottom: hasBeenDragged ? 'auto' : undefined,
          top: hasBeenDragged ? `${position.y}px` : undefined,
          transform: hasBeenDragged ? 'none' : 'translateX(-50%)',
          userSelect: 'none',
          willChange: isDragging ? 'transform' : 'auto',
          cursor: isDragging ? 'grabbing' : 'default',
        }}
      >
        <div className={classes.dragHandle} onMouseDown={handleMouseDown} onTouchStart={handleTouchStart} />
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', pointerEvents: isDragging ? 'none' : 'auto' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={enabledKeys.indexOf('dev') > -1}
                onChange={() => toggleStage('dev')}
                value="dev"
                sx={{
                  color: 'rgba(16, 185, 129, 0.3)',
                  padding: '6px',
                  '&.Mui-checked': {
                    color: DEV_COLOR,
                  },
                  '& .MuiSvgIcon-root': {
                    fontSize: '1.3rem',
                    borderRadius: '4px',
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(16, 185, 129, 0.08)',
                  },
                }}
              />
            }
            label="Dev"
            sx={{
              margin: 0,
              '& .MuiFormControlLabel-label': {
                fontSize: '0.875rem',
                fontWeight: 500,
                color: enabledKeys.indexOf('dev') > -1 ? DEV_COLOR : 'text.secondary',
                transition: 'color 0.2s ease',
              },
            }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={enabledKeys.indexOf('test') > -1}
                onChange={() => toggleStage('test')}
                value="test"
                sx={{
                  color: 'rgba(245, 158, 11, 0.3)',
                  padding: '6px',
                  '&.Mui-checked': {
                    color: TEST_COLOR,
                  },
                  '& .MuiSvgIcon-root': {
                    fontSize: '1.3rem',
                    borderRadius: '4px',
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(245, 158, 11, 0.08)',
                  },
                }}
              />
            }
            label="Test"
            sx={{
              margin: 0,
              '& .MuiFormControlLabel-label': {
                fontSize: '0.875rem',
                fontWeight: 500,
                color: enabledKeys.indexOf('test') > -1 ? TEST_COLOR : 'text.secondary',
                transition: 'color 0.2s ease',
              },
            }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={enabledKeys.indexOf('prod') > -1}
                onChange={() => toggleStage('prod')}
                value="prod"
                sx={{
                  color: 'rgba(239, 68, 68, 0.3)',
                  padding: '6px',
                  '&.Mui-checked': {
                    color: PROD_COLOR,
                  },
                  '& .MuiSvgIcon-root': {
                    fontSize: '1.3rem',
                    borderRadius: '4px',
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(239, 68, 68, 0.08)',
                  },
                }}
              />
            }
            label="Prod"
            sx={{
              margin: 0,
              '& .MuiFormControlLabel-label': {
                fontSize: '0.875rem',
                fontWeight: 500,
                color: enabledKeys.indexOf('prod') > -1 ? PROD_COLOR : 'text.secondary',
                transition: 'color 0.2s ease',
              },
            }}
          />
        </div>
      </div>
      <Grid container rowSpacing={2} columnSpacing={2}>
        <Widget
          id="elastic-cpu"
          size="sm"
          variant="graph"
          heading="ElasticSearch CPU"
          options="logging/metrics/elastic/options"
          endpoint="logging/metrics/elastic/search"
          params={baseWidgetParams}
          subheading="% of elastic search cpu used for the last day."
          showMetric={false}
          frequency="multi"
          showTimeRange={true}
          stages={enabledKeys}
        />
        <Widget
          id="cost"
          size="sm"
          variant="chart"
          heading="AWS Cost (USD)"
          options="logging/metrics/cost/options"
          endpoint="logging/metrics/cost/search"
          params={baseWidgetParams}
          subheading="Amount spent on AWS for the last week."
          showMetric={false}
          frequency="multi"
          showTimeRange={true}
          stages={enabledKeys}
        />
        <Widget
          id="lambda-duration"
          size="sm"
          variant="graph"
          heading="Lambda Function Duration"
          options="logging/metrics/duration/options"
          endpoint="logging/metrics/duration/search"
          params={baseWidgetParams}
          subheading="Average duration of lambda functions for the last day"
          showMetric={false}
          frequency="multi"
          showTimeRange={true}
          stages={enabledKeys}
        />
        <Widget
          id="lambda-invocations"
          size="sm"
          variant="graph"
          heading="Lambda Function Invocations"
          options="logging/metrics/invocations/options"
          endpoint="logging/metrics/invocations/search"
          params={baseWidgetParams}
          subheading="Number of lambda function invocations for the last day."
          showMetric={false}
          frequency="multi"
          showTimeRange={true}
          stages={enabledKeys}
        />
        <Widget
          id="sms"
          size="sm"
          variant="chart"
          heading="Outbound Sms Messages"
          options="logging/metrics/sms/options"
          endpoint="logging/metrics/sms/search"
          params={baseWidgetParams}
          subheading="Number of outbound sms messages for the last day."
          showMetric={false}
          frequency="multi"
          showTimeRange={true}
          stages={enabledKeys}
        />
        <Widget
          id="iops"
          size="sm"
          variant="graph"
          heading="ES Read/Write IOPS"
          options="logging/metrics/iops/options"
          endpoint="logging/metrics/iops/search"
          params={baseWidgetParams}
          subheading="Elasticsearch input/output operations per second for the last day."
          showMetric={false}
          frequency="multi"
          showTimeRange={true}
          stages={enabledKeys}
        />
      </Grid>
    </Root>
  )
}

export default Home
